import React, { useState, useEffect, useMemo } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import api from '../api/axios';
import { useNavigate } from 'react-router-dom';

function AutocompleteProveedor({
  value,
  onChange,
  onPick,
  options = [],
  loading,
  placeholder = 'Escribí o seleccioná'
}) {
  const [abierto, setAbierto] = useState(false);
  const [highlight, setHighlight] = useState(-1);

  const sugerencias = useMemo(() => {
    const q = (value || '').toLowerCase().trim();
    if (!q) return options.slice(0, 8);
    return options
      .filter(o => (o?.nombre || '').toLowerCase().includes(q))
      .slice(0, 8);
  }, [value, options]);

  const pick = (sug) => {
    onChange(sug?.nombre || '');
    onPick?.(sug || null);
    setAbierto(false);
    setHighlight(-1);
  };

  const onKeyDown = (e) => {
    if (!abierto && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
      setAbierto(true);
      return;
    }
    if (!sugerencias.length) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight(h => (h + 1) % sugerencias.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight(h => (h - 1 + sugerencias.length) % sugerencias.length);
    } else if (e.key === 'Enter') {
      if (highlight >= 0) {
        e.preventDefault();
        pick(sugerencias[highlight]);
      }
    } else if (e.key === 'Escape') {
      setAbierto(false);
      setHighlight(-1);
    }
  };

  return (
    <div style={{ position: 'relative' }}>
      <input
        type="text"
        className="form-control"
        value={value}
        onChange={(e) => { onChange(e.target.value); setAbierto(true); }}
        onFocus={() => setAbierto(true)}
        onBlur={() => setTimeout(() => setAbierto(false), 120)}
        onKeyDown={onKeyDown}
        placeholder={loading ? 'Cargando proveedores...' : placeholder}
        autoComplete="off"
        required
      />
      {abierto && sugerencias.length > 0 && (
        <ul
          className="list-group"
          style={{
            position: 'absolute',
            zIndex: 10,
            width: '100%',
            top: '100%',
            left: 0,
            maxHeight: 220,
            overflowY: 'auto',
            boxShadow: '0 6px 18px rgba(0,0,0,.08)'
          }}
        >
          {sugerencias.map((sug, i) => (
            <li
              key={sug.id ?? `${sug.nombre}-${i}`}
              className={`list-group-item ${i === highlight ? 'active' : ''}`}
              style={{ cursor: 'pointer' }}
              onMouseEnter={() => setHighlight(i)}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => pick(sug)}
              title={sug.codigo ? `Código: ${sug.codigo}` : undefined}
            >
              {sug.nombre}{sug.codigo ? ` — ${sug.codigo}` : ''}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

const SolicitarTurnoProveedor = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [fechaTurno, setFechaTurno] = useState(null);
  const [horasDisponibles, setHorasDisponibles] = useState([]);
  const [horaDesde, setHoraDesde] = useState('');
  const [horaHasta, setHoraHasta] = useState('');
  const [fechasDisponibles, setFechasDisponibles] = useState([]);
  const [observaciones, setObservaciones] = useState('');
  const [mensaje, setMensaje] = useState('');
  const [error, setError] = useState('');
  const [proveedoresLista, setProveedoresLista] = useState([]);
  const [cargandoProv, setCargandoProv] = useState(false);

  const navigate = useNavigate();

  const [proveedor, setProveedor] = useState({
    nombre: '',
    codigo: '',
    requiere_pdf: false,
    facturas: '',
    orden_compra: '',
    remito: '',
    documentos: [],
    bultos: { heladera: 0, caja: 0, bolsa: 0, pallet: 0, unidad: 0 }
  });

  useEffect(() => {
    const fetchDisponibilidad = async () => {
      try {
        const responseFechas = await api.get('/turnos/disponibles');
        setFechasDisponibles((responseFechas.data.fechas || []).map(fecha => new Date(fecha)));
      } catch (error) {
        console.error('❌ Error al obtener disponibilidad:', error);
        setError('Error al obtener disponibilidad de turnos.');
      }
    };

    const fetchProveedores = async () => {
      setCargandoProv(true);
      try {
        const token = localStorage.getItem('token');
        const { data } = await api.get('/turnos/proveedores', {
          headers: { Authorization: `Bearer ${token}` }
        });
        setProveedoresLista((data.proveedores || []).map(p => ({
          id: p.id,
          nombre: p.nombre,
          codigo: p.codigo || p.sap_id || '',
          requiere_pdf: !!p.requiere_pdf
        })));
      } catch (err) {
        console.error('❌ Error al obtener proveedores:', err);
        setError('No se pudo cargar la lista de proveedores.');
      } finally {
        setCargandoProv(false);
      }
    };

    fetchDisponibilidad();
    fetchProveedores();
  }, []);

  const handleFechaChange = async (date) => {
    setFechaTurno(date);
    setHorasDisponibles([]);
    setHoraDesde('');
    setHoraHasta('');

    try {
      const response = await api.get(`/turnos/disponibles?fecha=${date.toISOString().split('T')[0]}`);
      setHorasDisponibles(Array.isArray(response.data.horas) ? response.data.horas : []);
    } catch (error) {
      setError('Error al obtener horarios para la fecha seleccionada.');
    }
  };

  const handleHoraDesdeChange = (horaSeleccionada) => {
    setHoraDesde(horaSeleccionada);
    const [h, m] = horaSeleccionada.split(':').map(Number);
    const date = new Date();
    date.setHours(h);
    date.setMinutes(m + 30);
    setHoraHasta(date.toTimeString().slice(0, 5));
  };

  const handleProveedorChange = (field, value) => {
    setProveedor(prev => {
      const next = { ...prev, [field]: value };
      if (field === 'nombre') {
        next.codigo = '';
        next.requiere_pdf = false;
      }
      return next;
    });
  };

  const handleProveedorPick = (sug) => {
    setProveedor(prev => ({
      ...prev,
      nombre: sug?.nombre || '',
      codigo: sug?.codigo || '',
      requiere_pdf: !!sug?.requiere_pdf
    }));
  };

  const handleBultoChange = (tipo, value) => {
    setProveedor(prev => ({
      ...prev,
      bultos: {
        ...prev.bultos,
        [tipo]: Math.max(0, parseInt(value) || 0)
      }
    }));
  };

  const handleFileChange = (files) => {
    setProveedor(prev => ({ ...prev, documentos: Array.from(files) }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setMensaje('');

    const confirmar = window.confirm('¿Estás seguro de que querés solicitar el turno?');
    if (!confirmar) return;

    setIsSubmitting(true);

    if (!fechaTurno || !horaDesde || !horaHasta) {
      setError('Debe seleccionar una fecha y un rango horario.');
      setIsSubmitting(false);
      return;
    }

    if (horaDesde >= horaHasta) {
      setError('La hora de inicio debe ser menor que la hora de fin.');
      setIsSubmitting(false);
      return;
    }

    const token = localStorage.getItem('token');
    if (!token) {
      setError('No estás autenticado.');
      setIsSubmitting(false);
      return;
    }

    const archivosInvalidos = (proveedor.documentos || []).filter(file => file.type !== 'application/pdf');
    if (archivosInvalidos.length > 0) {
      setError('Uno o más archivos seleccionados no son PDF.');
      setIsSubmitting(false);
      return;
    }

    if (
      proveedor.requiere_pdf &&
      (!proveedor.documentos || proveedor.documentos.length === 0)
    ) {
      setError('Para este proveedor es obligatorio adjuntar al menos un archivo PDF.');
      setIsSubmitting(false);
      return;
    }

    const proveedoresJSON = JSON.stringify([{
      nombre: proveedor.nombre || '',
      codigo: proveedor.codigo || '',
      facturas: proveedor.facturas ? proveedor.facturas.split(',').map(f => f.trim()).filter(Boolean) : [],
      orden_compra: proveedor.orden_compra || '',
      remito: proveedor.remito || '',
      bultos: proveedor.bultos || {}
    }]);

    const formData = new FormData();
    formData.append('fecha_turno', fechaTurno.toISOString().split('T')[0]);
    formData.append('hora_desde', horaDesde);
    formData.append('hora_hasta', horaHasta);
    formData.append('observaciones', observaciones || '');
    formData.append('proveedor_bultos', proveedoresJSON);

    if (proveedor.documentos && proveedor.documentos.length > 0) {
      proveedor.documentos.forEach((file) => {
        formData.append('documentos', file);
      });
    }

    try {
      await api.post('/turnos', formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      setMensaje('Turno solicitado con éxito. Pendiente de aprobación.');
      setTimeout(() => navigate('/dashboard'), /* 2000 */);
    } catch (error) {
      console.error('❌ Error al solicitar turno:', error);
      setError(error.response?.data?.error || 'Error al solicitar el turno.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="container mt-5">
      <button className="btn btn-secondary" onClick={() => navigate('/dashboard')}>⬅ Volver al Dashboard</button>
      <br /><br />
      <h2>Solicitar Turno</h2>
      {error && <div className="alert alert-danger">{error}</div>}
      {mensaje && <div className="alert alert-success">{mensaje}</div>}

      <form onSubmit={handleSubmit}>
        <label>Fecha del Turno</label><br />
        <DatePicker
          selected={fechaTurno}
          onChange={handleFechaChange}
          includeDates={fechasDisponibles}
          dateFormat="yyyy-MM-dd"
          className="form-control"
          required
        />

        <br />
        <label>Hora Desde</label>
        <select className="form-control" value={horaDesde} onChange={(e) => handleHoraDesdeChange(e.target.value)} required>
          <option value="">Selecciona una hora</option>
          {horasDisponibles.map((hora, index) => (
            <option key={index} value={hora}>{hora}</option>
          ))}
        </select>

        <label>Observaciones (opcional)</label>
        <textarea
          className="form-control"
          value={observaciones}
          onChange={(e) => setObservaciones(e.target.value)}
          rows="3"
          maxLength="140"
          placeholder="Ingrese observaciones..."
        />
        <hr />

        <div className="border p-3 mb-3">
          <h5>Datos del Proveedor</h5>

          <label>Nombre del Proveedor</label>
          <AutocompleteProveedor
            value={proveedor.nombre}
            onChange={(nombre) => handleProveedorChange('nombre', nombre)}
            onPick={handleProveedorPick}
            options={proveedoresLista}
            loading={cargandoProv}
            placeholder="Escribí o seleccioná un proveedor"
          />
          {proveedor.codigo ? (
            <small className="text-muted d-block">Código seleccionado: {proveedor.codigo}</small>
          ) : null}

          {proveedor.requiere_pdf && (
            <div className="alert alert-warning mt-2 p-2">
              Para este proveedor la carga de documentos PDF es obligatoria.
            </div>
          )}

          <br />
          <input
            type="text"
            className="form-control"
            placeholder="Facturas (separadas por coma)"
            value={proveedor.facturas}
            onChange={(e) => handleProveedorChange('facturas', e.target.value)}
            required
          />
          <br />
          <input
            type="text"
            className="form-control"
            placeholder="Orden de Compra (opcional)"
            value={proveedor.orden_compra}
            onChange={(e) => handleProveedorChange('orden_compra', e.target.value)}
          />
          <br />
          <input
            type="text"
            className="form-control"
            placeholder="Número de Remito"
            value={proveedor.remito}
            onChange={(e) => handleProveedorChange('remito', e.target.value)}
          />
          <br />

          {/* 🆕 campo archivos con indicación de obligatorio */}
          <label>
            Facturas / Remitos en PDF
            {proveedor.requiere_pdf && <span className="text-danger"> *</span>}
          </label>
          <input
            type="file"
            className="form-control"
            accept="application/pdf"
            multiple
            onChange={(e) => handleFileChange(e.target.files)}
            required={proveedor.requiere_pdf}
          />
          {proveedor.requiere_pdf && (
            <small className="text-muted">
              Para este proveedor es obligatorio adjuntar al menos un PDF.
            </small>
          )}
          <br />

          <label>Tipos de Bultos</label>
          <div className="row">
            {Object.entries(proveedor.bultos).map(([tipo, cantidad]) => (
              <div key={tipo} className="col-md-2 d-flex flex-column align-items-center">
                <label>{tipo.charAt(0).toUpperCase() + tipo.slice(1)}</label>
                <input
                  type="number"
                  className="form-control text-center"
                  min="0"
                  value={cantidad}
                  onChange={(e) => handleBultoChange(tipo, e.target.value)}
                />
              </div>
            ))}
          </div>
        </div>

        <button type="submit" className="btn btn-primary mt-3" disabled={isSubmitting}>
          {isSubmitting ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
              Enviando...
            </>
          ) : (
            'Solicitar Turno'
          )}
        </button>
      </form>
    </div>
  );
};

export default SolicitarTurnoProveedor;
